import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Feather, Sparkles, HandHeart, MessageCircle, Flag, Trash2, Info, Shield, Loader2, Send, Inbox as InboxIcon } from "lucide-react";

import { _t, _locale } from "@/lib/i18n";
const REACTION_META = {
  ti_sento:      { label: _t("Ti sento"),     icon: Heart,     color: "text-rose-400"  },
  pace:          { label: _t("Pace"),         icon: Feather,   color: "text-sky-400"   },
  ispirante:     { label: _t("Ispirante"),    icon: Sparkles,  color: "text-amber-300" },
  ti_abbraccio:  { label: _t("Ti abbraccio"), icon: HandHeart, color: "text-emerald-300" },
};

function DisclaimerBanner({ text }) {
  return (
    <div data-testid="bacheca-disclaimer" className="rounded-2xl border border-amber-400/30 bg-amber-500/5 p-4 flex gap-3">
      <Shield className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-amber-100/90 leading-relaxed">
        <div className="font-semibold text-amber-200 mb-1">{_t("Avviso — leggi prima di pubblicare")}</div>
        {text}
      </div>
    </div>
  );
}

function ConsentModal({ visible, disclaimerText, onAccept, onCancel, submitting }) {
  if (!visible) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div data-testid="bacheca-consent-modal" className="max-w-lg w-full rounded-2xl bg-[#0F1A2E] border border-white/10 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Info className="w-5 h-5 text-[#FF5722]" />
          <h2 className="text-xl text-white font-display">{_t("Prima di pubblicare")}</h2>
        </div>
        <p className="text-sm text-white/80 leading-relaxed">{disclaimerText}</p>
        <div className="flex justify-end gap-3 pt-2">
          <button data-testid="consent-cancel-btn" className="btn-ghost" onClick={onCancel} disabled={submitting}>{_t("Annulla")}</button>
          <button data-testid="consent-accept-btn" className="btn-primary" onClick={onAccept} disabled={submitting}>{_t("{0} Accetto e pubblico", submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin inline" />)}</button>
        </div>
      </div>
    </div>
  );
}

function Composer({ meta, onPost, busy }) {
  const [category, setCategory] = useState("emozioni");
  const [text, setText] = useState("");
  const chars = text.length;
  const canPost = text.trim().length >= 3 && chars <= (meta?.limits?.post_max_chars || 4000);

  return (
    <div data-testid="bacheca-composer" className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {(meta?.categories || []).map((c) => (
          <button
            key={c.id}
            data-testid={`composer-cat-${c.id}`}
            onClick={() => setCategory(c.id)}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${
              category === c.id ? "border-[#FF5722] text-[#FF5722] bg-[#FF5722]/10" : "border-white/15 text-white/60 hover:text-white"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      <textarea
        data-testid="composer-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={_t("Racconta il tuo ricordo, una sorpresa, un traguardo o una verità nascosta…")}
        rows={4}
        maxLength={meta?.limits?.post_max_chars || 4000}
        className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF5722]/60"
      />
      <div className="flex items-center justify-between text-xs text-white/40">
        <span>{chars} / {meta?.limits?.post_max_chars || 4000}</span>
        <button
          data-testid="composer-submit"
          onClick={() => canPost && onPost({ category, text: text.trim() }, () => setText(""))}
          disabled={!canPost || busy}
          className="btn-primary !py-2 !px-4 disabled:opacity-40"
        >{_t("{0} Pubblica", busy && <Loader2 className="w-4 h-4 mr-1 animate-spin inline" />)}</button>
      </div>
    </div>
  );
}

function CategoryFilter({ meta, active, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        data-testid="filter-cat-all"
        onClick={() => onChange(null)}
        className={`px-3 py-1 rounded-full text-xs border ${active === null ? "border-white/60 text-white" : "border-white/15 text-white/60"}`}
      >{_t("Tutte")}</button>
      {(meta?.categories || []).map((c) => (
        <button
          key={c.id}
          data-testid={`filter-cat-${c.id}`}
          onClick={() => onChange(c.id)}
          className={`px-3 py-1 rounded-full text-xs border ${active === c.id ? "border-[#FF5722] text-[#FF5722]" : "border-white/15 text-white/60 hover:text-white"}`}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

function ReactionRow({ post, onReact }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {Object.entries(REACTION_META).map(([key, cfg]) => {
        const Icon = cfg.icon;
        const active = post.viewer_reaction === key;
        const count = post.reactions?.[key] || 0;
        return (
          <button
            key={key}
            data-testid={`react-${key}-${post.id}`}
            onClick={() => onReact(post, active ? null : key)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border transition-colors ${
              active ? `${cfg.color} border-current bg-white/5` : "text-white/50 border-white/10 hover:text-white"
            }`}
            title={cfg.label}
          >
            <Icon className="w-3.5 h-3.5" />
            <span className="text-[11px]">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

function CommentSection({ postId, canDeleteAny, onAfterChange }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/bulletin/posts/${postId}/comments`);
      setComments(data.comments);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [postId]);

  const send = async () => {
    if (text.trim().length < 1) return;
    setSending(true);
    try {
      const { data } = await api.post(`/bulletin/posts/${postId}/comments`, { text: text.trim() });
      setComments((c) => [...c, data]);
      setText("");
      onAfterChange && onAfterChange();
    } catch (e) { toast.error(formatApiError(e)); }
    setSending(false);
  };

  const remove = async (cid) => {
    if (!window.confirm(_t("Eliminare questo commento?"))) return;
    try {
      await api.delete(`/bulletin/comments/${cid}`);
      setComments((c) => c.filter((x) => x.id !== cid));
      onAfterChange && onAfterChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/5 space-y-3">
      {loading ? (
        <div className="text-xs text-white/40">{_t("Carico commenti…")}</div>
      ) : comments.length === 0 ? (
        <div className="text-xs text-white/40">{_t("Nessun commento. Rompi il ghiaccio.")}</div>
      ) : (
        comments.map((c) => (
          <div key={c.id} data-testid={`comment-${c.id}`} className="text-sm bg-white/[0.02] rounded-xl px-3 py-2">
            <div className="flex justify-between items-center">
              <span className="text-white/70 text-xs font-semibold">{c.author_display_name}</span>
              <span className="text-white/30 text-[10px]">{new Date(c.created_at).toLocaleString(_locale())}</span>
            </div>
            <div className="text-white/90 mt-1 whitespace-pre-wrap">{c.text}</div>
            {(c.can_delete || canDeleteAny) && (
              <button
                data-testid={`comment-del-${c.id}`}
                onClick={() => remove(c.id)}
                className="mt-1 text-[10px] text-white/40 hover:text-rose-400 inline-flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" />{" "}{_t("Elimina")}</button>
            )}
          </div>
        ))
      )}
      <div className="flex gap-2">
        <input
          data-testid={`comment-input-${postId}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={_t("Scrivi un commento gentile…")}
          className="flex-1 bg-black/20 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF5722]/60"
        />
        <button
          data-testid={`comment-send-${postId}`}
          onClick={send}
          disabled={sending || text.trim().length < 1}
          className="btn-primary !py-2 !px-3 disabled:opacity-40"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function PostCard({ post, isOwner, isAdmin, onReact, onDelete, onReport }) {
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comment_count || 0);

  return (
    <article data-testid={`post-${post.id}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-white font-semibold" data-testid={`post-author-${post.id}`}>{post.author_display_name}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">
            {post.category_label} · {new Date(post.created_at).toLocaleString(_locale())}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(isOwner || isAdmin) && (
            <button data-testid={`post-del-${post.id}`} onClick={() => onDelete(post)} className="text-white/40 hover:text-rose-400" title={_t("Elimina")}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {!isOwner && (
            <button data-testid={`post-report-${post.id}`} onClick={() => onReport(post)} className="text-white/40 hover:text-amber-400" title={_t("Segnala")}>
              <Flag className="w-4 h-4" />
            </button>
          )}
        </div>
      </header>
      <div data-testid={`post-text-${post.id}`} className="text-white/95 whitespace-pre-wrap leading-relaxed">
        {post.text}
      </div>
      <footer className="flex items-center justify-between">
        <ReactionRow post={post} onReact={onReact} />
        <button
          data-testid={`post-toggle-comments-${post.id}`}
          onClick={() => setShowComments((s) => !s)}
          className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white"
        >
          <MessageCircle className="w-4 h-4" /> {commentCount}{" "}{_t("commenti")}</button>
      </footer>
      {showComments && (
        <CommentSection
          postId={post.id}
          canDeleteAny={isAdmin}
          onAfterChange={() => setCommentCount((c) => c)}
        />
      )}
    </article>
  );
}

function Inbox({ replies, onMarkRead }) {
  if (!replies.length) {
    return <div data-testid="inbox-empty" className="text-sm text-white/40">{_t("Nessuna cartolina di ritorno ricevuta.")}</div>;
  }
  return (
    <div className="space-y-3">
      {replies.map((r) => (
        <div key={r.id} data-testid={`inbox-item-${r.id}`}
             className={`rounded-2xl border p-4 ${r.read ? "border-white/10 bg-white/[0.02]" : "border-[#FF5722]/30 bg-[#FF5722]/5"}`}>
          <div className="flex items-center justify-between">
            <div className="text-sm text-white font-semibold">{r.from_display_name}</div>
            <div className="text-[10px] text-white/40">{new Date(r.created_at).toLocaleString(_locale())}</div>
          </div>
          <div className="text-white/90 text-sm mt-2 whitespace-pre-wrap">{r.text}</div>
          {!r.read && (
            <button data-testid={`inbox-mark-${r.id}`} onClick={() => onMarkRead(r)} className="mt-2 text-xs text-[#FF5722] hover:underline">{_t("Segna come letta")}</button>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Bacheca() {
  const [meta, setMeta] = useState(null);
  const [posts, setPosts] = useState([]);
  const [replies, setReplies] = useState([]);
  const [filter, setFilter] = useState(null);
  const [tab, setTab] = useState("feed");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [pendingPost, setPendingPost] = useState(null); // {payload, clear}
  const [consentVisible, setConsentVisible] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: m }, { data: userData }] = await Promise.all([
          api.get("/bulletin/meta"),
          api.get("/auth/me"),
        ]);
        setMeta(m);
        setMe(userData);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const params = filter ? { category: filter } : {};
      const { data } = await api.get("/bulletin/posts", { params });
      setPosts(data.posts);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const loadInbox = async () => {
    try {
      const { data } = await api.get("/private-replies/inbox");
      setReplies(data.replies);
    } catch (e) { /* silent: endpoint always present */ }
  };

  useEffect(() => { if (meta) loadPosts(); /* eslint-disable-next-line */ }, [meta, filter]);
  useEffect(() => { if (meta) loadInbox(); /* eslint-disable-next-line */ }, [meta]);

  const submitPost = async (payload, clear, withConsent = false) => {
    setPosting(true);
    try {
      const body = { ...payload };
      if (withConsent && meta?.disclaimer_version) body.accepted_disclaimer_version = meta.disclaimer_version;
      const { data } = await api.post("/bulletin/posts", body);
      setPosts((p) => [data, ...p]);
      clear && clear();
      setConsentVisible(false);
      setPendingPost(null);
      toast.success(_t("Pubblicato!"));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 428) {
        setPendingPost({ payload, clear });
        setConsentVisible(true);
      } else {
        toast.error(formatApiError(e));
      }
    }
    setPosting(false);
  };

  const react = async (post, reaction) => {
    // ottimistica
    const prev = post.viewer_reaction;
    setPosts((p) => p.map((x) => {
      if (x.id !== post.id) return x;
      const counts = { ...x.reactions };
      if (prev) counts[prev] = Math.max(0, counts[prev] - 1);
      if (reaction) counts[reaction] = (counts[reaction] || 0) + 1;
      return { ...x, viewer_reaction: reaction, reactions: counts };
    }));
    try {
      await api.post(`/bulletin/posts/${post.id}/reactions`, { reaction });
    } catch (e) {
      toast.error(formatApiError(e));
      loadPosts();
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm(_t("Eliminare questo post?"))) return;
    try {
      await api.delete(`/bulletin/posts/${post.id}`);
      setPosts((p) => p.filter((x) => x.id !== post.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reportPost = async (post) => {
    const reason = window.prompt(_t("Motivo della segnalazione (min 3 caratteri):"));
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/bulletin/posts/${post.id}/report`, { reason: reason.trim() });
      toast.success(_t("Segnalazione inviata ai moderatori."));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markReplyRead = async (r) => {
    try {
      await api.post(`/private-replies/${r.id}/read`);
      setReplies((rs) => rs.map((x) => x.id === r.id ? { ...x, read: true } : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const unreadReplies = useMemo(() => replies.filter((r) => !r.read).length, [replies]);

  return (
    <AppLayout>
      <div className="space-y-8">
        <header className="space-y-2">
          <h1 className="font-display text-4xl sm:text-5xl text-white">{_t("La Bacheca dei Ricordi")}</h1>
          <p className="text-white/60 text-sm max-w-2xl">{_t("Uno spazio interno dei Dreamer per condividere storie, riflessioni ed emozioni sulle capsule inviate o ricevute.")}</p>
        </header>

        <div className="flex gap-2">
          <button
            data-testid="tab-feed"
            onClick={() => setTab("feed")}
            className={`px-4 py-2 rounded-full text-sm border ${tab === "feed" ? "border-[#FF5722] text-[#FF5722] bg-[#FF5722]/10" : "border-white/15 text-white/70"}`}
          >{_t("Feed")}</button>
          <button
            data-testid="tab-inbox"
            onClick={() => setTab("inbox")}
            className={`px-4 py-2 rounded-full text-sm border inline-flex items-center gap-2 ${tab === "inbox" ? "border-[#FF5722] text-[#FF5722] bg-[#FF5722]/10" : "border-white/15 text-white/70"}`}
          >
            <InboxIcon className="w-4 h-4" />{_t("Cartoline ricevute")}{unreadReplies > 0 && (
              <span data-testid="inbox-badge" className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF5722] text-white text-[10px] font-bold flex items-center justify-center">
                {unreadReplies}
              </span>
            )}
          </button>
        </div>

        {tab === "feed" ? (
          <>
            <DisclaimerBanner text={meta?.disclaimer_text || ""} />
            <Composer meta={meta} onPost={(payload, clear) => submitPost(payload, clear)} busy={posting} />
            <CategoryFilter meta={meta} active={filter} onChange={setFilter} />
            {loading ? (
              <div className="text-white/50 text-sm">{_t("Carico il feed…")}</div>
            ) : posts.length === 0 ? (
              <div className="text-white/50 text-sm" data-testid="empty-feed">{_t("Ancora nessun ricordo condiviso in questa categoria.")}</div>
            ) : (
              <div className="space-y-4" data-testid="posts-list">
                {posts.map((p) => (
                  <PostCard
                    key={p.id}
                    post={p}
                    isOwner={me && p.author_id === me.id}
                    isAdmin={!!me?.is_admin}
                    onReact={react}
                    onDelete={deletePost}
                    onReport={reportPost}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <p className="text-white/60 text-sm">{_t("Cartoline di ritorno che hai ricevuto dai destinatari delle tue capsule aperte.")}</p>
            <Inbox replies={replies} onMarkRead={markReplyRead} />
          </div>
        )}

        <ConsentModal
          visible={consentVisible}
          disclaimerText={meta?.disclaimer_text || ""}
          submitting={posting}
          onCancel={() => { setConsentVisible(false); setPendingPost(null); }}
          onAccept={() => pendingPost && submitPost(pendingPost.payload, pendingPost.clear, true)}
        />
      </div>
    </AppLayout>
  );
}
